import { useLocation, useNavigate } from 'react-router-dom'
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card'
import { Button } from '@/components/ui/button'
import { Badge } from '@/components/ui/badge'
import { AlertCircle, Loader2 } from 'lucide-react'
import { useState } from 'react'
import { PropFirms } from './PropFirms'
import { ProfitsVerification } from './ProfitsVerification'
import { SubscriptionManagement } from './SubscriptionManagement'
import { TermsAcceptance } from './TermsAcceptance'
import { YomiLog } from './YomiLog'
import { SniperLog } from './SniperLog'
import { JifuAlerts } from './JifuAlerts'
import { AurouxAlerts } from './AurouxAlerts'
import { Toaster } from 'sonner'

export const Admin = () => {
  const location = useLocation()
  const navigate = useNavigate()
  const [isClosingAll, setIsClosingAll] = useState(false)
  
  const getActiveTab = () => {
    if (location.pathname.includes('propfirms')) return 'propfirms'
    if (location.pathname.includes('profits')) return 'profits'
    if (location.pathname.includes('subscriptions')) return 'subscriptions'
    if (location.pathname.includes('emergency')) return 'emergency'
    if (location.pathname.includes('analytics')) return 'analytics'
    if (location.pathname.includes('terms')) return 'terms'
    if (location.pathname.includes('yomilog')) return 'yomilog'
    if (location.pathname.includes('sniperlog')) return 'sniperlog'
    if (location.pathname.includes('auroux')) return 'auroux'
    if (location.pathname.includes('alerts')) return 'alerts'
    return 'users'
  }
  
  const activeTab = getActiveTab()
  
  const handleCloseAllPositions = async () => {
    if (!confirm('⚠️ ATTENZIONE: Questa azione chiuderà TUTTE le posizioni aperte per TUTTI gli utenti. Sei sicuro?')) return
    
    setIsClosingAll(true)
    try {
      // TODO: Implementare chiamata API per chiudere tutte le posizioni
      // await adminService.closeAllPositions()
      await new Promise(resolve => setTimeout(resolve, 2000)) // Simulazione temporanea
      alert('Tutte le posizioni sono state chiuse con successo')
    } catch (err) {
      alert('Errore durante la chiusura delle posizioni: ' + (err instanceof Error ? err.message : 'Unknown error'))
    } finally {
      setIsClosingAll(false)
    }
  }

  const tabs = [
    { id: 'users', label: 'User Accounts' },
    { id: 'subscriptions', label: 'Abbonamenti' },
    { id: 'propfirms', label: 'Prop Firms' },
    { id: 'profits', label: 'Profitti Verificati' },
    { id: 'terms', label: 'Contratto' },
    { id: 'yomilog', label: 'Log Yomi' },
    { id: 'sniperlog', label: 'Sniper 🎯' },
    { id: 'alerts', label: 'Alert Sempai 先' },
    { id: 'auroux', label: 'Alert Auroux' },
    { id: 'emergency', label: 'Emergency' },
    { id: 'analytics', label: 'Analytics' }
  ]

  return (
    <>
      <Toaster position="top-right" />
      <div className="p-6 space-y-6">
        <div>
          <h1 className="text-3xl font-bold">Administration</h1>
          <p className="text-muted-foreground">System configuration and management</p>
        </div>

        <div className="flex space-x-2 border-b overflow-x-auto">
          {tabs.map(tab => (
            <button
              key={tab.id}
              onClick={() => navigate(`/admin/${tab.id}`)}
              className={`px-4 py-2 border-b-2 transition-colors whitespace-nowrap ${
                activeTab === tab.id 
                  ? 'border-primary text-primary font-medium' 
                  : 'border-transparent text-muted-foreground hover:text-foreground'
              }`}
            >
              {tab.label}
            </button>
          ))}
        </div>

        {activeTab === 'propfirms' ? (
          <PropFirms />
        ) : activeTab === 'profits' ? (
          <ProfitsVerification />
        ) : activeTab === 'subscriptions' ? (
          <SubscriptionManagement />
        ) : activeTab === 'terms' ? (
          <TermsAcceptance />
        ) : activeTab === 'yomilog' ? (
          <YomiLog />
        ) : activeTab === 'sniperlog' ? (
          <SniperLog />
        ) : activeTab === 'alerts' ? (
          <JifuAlerts />
        ) : activeTab === 'auroux' ? (
          <AurouxAlerts />
        ) : activeTab === 'emergency' ? (
        <Card className="border-red-500">
          <CardHeader>
            <CardTitle className="flex items-center space-x-2 text-red-500">
              <AlertCircle className="w-5 h-5" />
              <span>Emergency Controls</span>
            </CardTitle>
            <CardDescription>Close all positions across all users</CardDescription>
          </CardHeader>
          <CardContent className="space-y-4">
            <div className="p-4 bg-red-500/10 rounded-lg border border-red-500">
              <p className="text-sm font-medium mb-2">Warning</p>
              <p className="text-sm text-muted-foreground">
                This will close ALL open positions for ALL users immediately. Use only in emergency situations.
              </p>
            </div>
            <Button 
              variant="destructive" 
              className="w-full" 
              disabled={isClosingAll}
              onClick={handleCloseAllPositions}
            >
              {isClosingAll ? (
                <>
                  <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                  Chiusura in corso...
                </>
              ) : (
                'Chiudi Tutte le Posizioni'
              )}
            </Button>
          </CardContent>
        </Card>
      ) : (
        <Card>
          <CardHeader>
            <CardTitle>
              {activeTab === 'users' ? 'User Accounts' : 'Analytics Dashboard'}
            </CardTitle>
          </CardHeader>
          <CardContent>
            <div className="space-y-2">
              <div className="flex items-center justify-between p-3 border rounded-lg">
                <div>
                  <p className="font-semibold">
                    {activeTab === 'users' ? 'user@example.com' : 'Challenge Phase - $100k'}
                  </p>
                  <p className="text-sm text-muted-foreground">
                    {activeTab === 'users' ? 'User' : 'Lots: 0.5'}
                  </p>
                </div>
                <Badge>{activeTab === 'users' ? 'Active' : 'Configured'}</Badge>
              </div>
            </div>
          </CardContent>
        </Card>
        )}
      </div>
    </>
  )
}
